import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { BRANDS, getBrand } from "@/data/brands";
import { MaskLines, ClipImage, Reveal, EASE } from "@/components/Motion";
import { BrandReel } from "@/components/BrandReel";
import { BrandInstagram } from "@/components/BrandInstagram";

const Cover = ({ b }) => {
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 800], ["0%", "20%"]);
  return (
    <motion.div className="relative h-[60vh] overflow-hidden lg:col-span-5 lg:h-[82vh]" initial={{ clipPath: "inset(100% 0% 0% 0%)" }} animate={{ clipPath: "inset(0% 0% 0% 0%)" }} transition={{ duration: 1.4, ease: EASE, delay: 0.2 }}>
      <motion.img style={{ y }} src={b.cover} alt={`${b.name} campaign`} className="h-[120%] w-full object-cover object-top" />
    </motion.div>
  );
};

export default function BrandPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const b = getBrand(slug);
  if (!b) return <Navigate to="/" replace />;
  const idx = BRANDS.indexOf(b);
  const next = BRANDS[(idx + 1) % BRANDS.length];
  const label = b.name === "Guess" ? `Guess ${b.sub}` : b.name;

  return (
    <main data-testid={`brand-page-${b.slug}`} className="pt-28">
      <section className="mx-auto grid max-w-[1600px] grid-cols-1 gap-12 px-6 sm:px-10 lg:grid-cols-12 lg:px-14">
        <div className="flex flex-col justify-between lg:col-span-7">
          <Link to="/#brands" data-testid="brand-back-link" className="group inline-flex w-fit items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-[#d6d3cd]">
            <ArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-1" /> All brands
          </Link>
          <div className="mt-16">
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#c8a97e]">{String(idx + 1).padStart(2, "0")} / {b.category} · {b.origin}</p>
            <h1 data-testid="brand-title" className="mt-6 font-display text-[18vw] uppercase leading-[0.85] sm:text-[12vw] lg:text-[9vw] 2xl:text-[150px]">
              <MaskLines key={b.slug} delay={0.1} lines={[b.name, <em key="s" className="text-[0.45em] normal-case text-[#c8a97e]">{b.sub}</em>]} />
            </h1>
          </div>
          <Reveal className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2">
            {b.story.map((p, i) => <p key={i} className="text-base leading-relaxed text-[#c9c6c0]">{p}</p>)}
          </Reveal>
        </div>
        <Cover b={b} />
      </section>

      {b.reel && <BrandReel key={b.slug} reel={b.reel} label={label} />}
      <BrandInstagram key={`ig-${b.slug}`} slug={b.slug} label={label} />

      <section className="mx-auto mt-24 grid max-w-[1600px] grid-cols-1 gap-6 px-6 sm:px-10 md:grid-cols-12 lg:px-14">
        <ClipImage src={b.gallery[0]} alt={`${label} lookbook`} className="aspect-[4/5] md:col-span-5" imgClass="object-top" />
        <div className="flex flex-col justify-end gap-8 md:col-span-6 md:col-start-7">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#b4b2ad]">In the showroom</p>
            <p className="mt-4 font-display text-4xl sm:text-5xl">{b.collections}</p>
          </Reveal>
          <Reveal delay={0.1}>
            <button data-testid="brand-book-btn" onClick={() => navigate("/#appointment", { state: { brand: label } })} className="group flex w-fit items-center gap-3 rounded-full bg-[#c8a97e] px-8 py-4 text-[13px] font-medium uppercase tracking-[0.16em] text-[#0a0b0d] transition-colors duration-300 hover:bg-[#f5f3ef]">
              Book a {label} appointment <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:rotate-45" />
            </button>
          </Reveal>
        </div>
      </section>

      <Link to={`/brands/${next.slug}`} data-testid="brand-next-link" data-cursor="view" className="group mx-auto mt-32 block max-w-[1600px] border-t border-white/10 px-6 py-16 sm:px-10 lg:px-14">
        <span className="font-mono text-xs uppercase tracking-[0.25em] text-[#b4b2ad]">Next house</span>
        <span className="mt-4 flex items-center justify-between font-display text-6xl transition-colors duration-500 group-hover:text-[#c8a97e] sm:text-8xl">
          {next.name} <ArrowUpRight className="h-12 w-12 transition-transform duration-500 group-hover:rotate-45" />
        </span>
      </Link>
    </main>
  );
}
